/***************************************************************************************
 * FileName      : QuadImagePlane.ts
 * Description   : 4개 꼭짓점 좌표로 정의된 사각형에 이미지를 렌더링하는 SDK 컴포넌트
 *                 - PlaneGeometry 꼭짓점 위치를 수정하여 임의의 사각형 형태 지원
 *                 - PlaneRenderer.ts 패턴을 그대로 따라 안정성 확보
 * Author        : (주)한국공학기술연구원 박은정
 * Created Date  : 2026.02.11
 * Modified Date : 2026.02.12 sRGB 색 공간 지정 (빛바랜 현상 수정)
 * Reference     : PlaneRenderer.ts, ImagePlane.ts
 ***************************************************************************************/

import { SceneComponent, ComponentInteractionType } from '../SceneComponent';

// ============================================================================
// 꼭짓점 좌표 타입
// ============================================================================

/** 2026.02.11 : 박은정 : 3D 좌표 */
interface Vec3 {
  x: number;
  y: number;
  z: number;
}

// ============================================================================
// 입력 타입 정의
// ============================================================================

type Inputs = {
  /** 이미지 소스 URL (data URI 또는 HTTP URL) */
  src: string | null;
  /** 텍스처 가로 해상도 (픽셀) */
  textureWidth: number;
  /** 텍스처 세로 해상도 (픽셀) */
  textureHeight: number;
  /** 좌측 상단 꼭짓점 (로컬 좌표) */
  topLeft: Vec3;
  /** 우측 상단 꼭짓점 (로컬 좌표) */
  topRight: Vec3;
  /** 좌측 하단 꼭짓점 (로컬 좌표) */
  bottomLeft: Vec3;
  /** 우측 하단 꼭짓점 (로컬 좌표) */
  bottomRight: Vec3;
  /** 투명 배경 사용 여부 */
  transparent: boolean;
  /** 불투명도 (0.0 ~ 1.0) */
  opacity: number;
  /** 표시 여부 */
  visible: boolean;
};

// ============================================================================
// QuadImagePlane 컴포넌트
// ============================================================================

/**
 * 2026.02.11 : 박은정 : 4개 꼭짓점으로 정의된 사각형에 이미지를 렌더링
 *
 * 접근 방식:
 *   - PlaneGeometry(1,1) 생성 후 꼭짓점 position 속성을 직접 수정
 *   - BufferGeometry 직접 생성 대신 PlaneRenderer.ts와 동일한 안전한 패턴 사용
 *   - PlaneGeometry 꼭짓점 순서:
 *     vertex 0 = topLeft,     vertex 1 = topRight,
 *     vertex 2 = bottomLeft,  vertex 3 = bottomRight
 */
class QuadImagePlane extends SceneComponent {
  private mesh: any = null;
  private pivotNode: any = null;
  private canvas: HTMLCanvasElement | null = null;
  private canvasTexture: any = null;
  private image: HTMLImageElement | null = null;

  inputs: Inputs = {
    src: null,
    textureWidth: 1024,
    textureHeight: 1024,
    topLeft: { x: -0.5, y: 0.5, z: 0 },
    topRight: { x: 0.5, y: 0.5, z: 0 },
    bottomLeft: { x: -0.5, y: -0.5, z: 0 },
    bottomRight: { x: 0.5, y: -0.5, z: 0 },
    transparent: true,
    opacity: 1.0,
    visible: true,
  };

  events = {
    [ComponentInteractionType.CLICK]: true,
  };

  /**
   * 2026.02.11 : 박은정 : 컴포넌트 초기화
   * - PlaneGeometry 생성 후 꼭짓점 위치 수정
   * - Canvas → CanvasTexture → Mesh 생성
   * - outputs.objectRoot 설정
   */
  onInit() {
    const THREE = this.context.three;

    // 2026.02.11 : 박은정 : 오프스크린 캔버스 생성
    this.canvas = document.createElement('canvas');
    this.canvas.width = this.inputs.textureWidth;
    this.canvas.height = this.inputs.textureHeight;

    // 2026.02.11 : 박은정 : 플레이스홀더 그리기
    this.drawPlaceholder('Loading...');

    // 2026.02.11 : 박은정 : CanvasTexture 생성
    this.canvasTexture = new THREE.CanvasTexture(this.canvas);
    // 2026.02.12 : 박은정 : sRGB 색 공간 지정 (renderer.outputEncoding = sRGBEncoding 환경에서 빛바랜 현상 방지)
    this.canvasTexture.encoding = THREE.sRGBEncoding;

    // 2026.02.11 : 박은정 : PlaneGeometry 생성 (PlaneRenderer.ts와 동일)
    const geometry = new THREE.PlaneGeometry(1.0, 1.0);

    // 2026.02.11 : 박은정 : 꼭짓점 위치를 4개 꼭짓점 좌표로 수정
    this.updateGeometryVertices(geometry);

    // 2026.02.11 : 박은정 : MeshBasicMaterial (PlaneRenderer.ts와 동일 패턴)
    const material = new THREE.MeshBasicMaterial({
      map: this.canvasTexture,
      transparent: this.inputs.transparent,
      opacity: this.inputs.opacity,
      side: THREE.DoubleSide,
    });

    // 2026.02.11 : 박은정 : Mesh 생성
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.visible = this.inputs.visible;

    // 2026.02.11 : 박은정 : 피벗 그룹 (PlaneRenderer.ts와 동일)
    this.pivotNode = new THREE.Group();
    this.pivotNode.add(this.mesh);

    // 2026.02.11 : 박은정 : outputs 설정 (PlaneRenderer.ts와 동일)
    this.outputs.objectRoot = this.pivotNode;
    this.outputs.collider = this.pivotNode;
    // 2026.02.11 : 박은정 : 디버깅 로그 제거

    // 2026.02.11 : 박은정 : 이미지 로드 시작
    this.loadImage();
  }

  /**
   * 2026.02.11 : 박은정 : PlaneGeometry 꼭짓점 위치를 4개 꼭짓점 좌표로 수정
   *
   * THREE.js PlaneGeometry(1,1) 꼭짓점 순서:
   *   vertex 0: (-0.5,  0.5, 0) → topLeft
   *   vertex 1: ( 0.5,  0.5, 0) → topRight
   *   vertex 2: (-0.5, -0.5, 0) → bottomLeft
   *   vertex 3: ( 0.5, -0.5, 0) → bottomRight
   */
  private updateGeometryVertices(geometry: any) {
    const posAttr = geometry.getAttribute('position');
    if (!posAttr) return;

    const { topLeft, topRight, bottomLeft, bottomRight } = this.inputs;

    // 2026.02.11 : 박은정 : 꼭짓점 0: 좌측 상단
    posAttr.setXYZ(0, topLeft.x, topLeft.y, topLeft.z);
    // 2026.02.11 : 박은정 : 꼭짓점 1: 우측 상단
    posAttr.setXYZ(1, topRight.x, topRight.y, topRight.z);
    // 2026.02.11 : 박은정 : 꼭짓점 2: 좌측 하단
    posAttr.setXYZ(2, bottomLeft.x, bottomLeft.y, bottomLeft.z);
    // 2026.02.11 : 박은정 : 꼭짓점 3: 우측 하단
    posAttr.setXYZ(3, bottomRight.x, bottomRight.y, bottomRight.z);

    posAttr.needsUpdate = true;
    geometry.computeVertexNormals();
  }

  /**
   * 2026.02.11 : 박은정 : 입력값 변경 시 처리
   */
  onInputsUpdated(oldInputs: Inputs) {
    // 2026.02.11 : 박은정 : 이미지 소스 변경
    if (oldInputs.src !== this.inputs.src) {
      this.loadImage();
    }

    // 2026.02.11 : 박은정 : 꼭짓점 좌표 변경 시 지오메트리 업데이트
    if (
      oldInputs.topLeft !== this.inputs.topLeft ||
      oldInputs.topRight !== this.inputs.topRight ||
      oldInputs.bottomLeft !== this.inputs.bottomLeft ||
      oldInputs.bottomRight !== this.inputs.bottomRight
    ) {
      if (this.mesh) {
        this.updateGeometryVertices(this.mesh.geometry);
      }
    }

    // 2026.02.11 : 박은정 : 머티리얼 속성 업데이트
    if (this.mesh) {
      this.mesh.visible = this.inputs.visible;
      const material = this.mesh.material;
      if (material) {
        material.opacity = this.inputs.opacity;
        material.transparent = this.inputs.transparent;
        material.needsUpdate = true;
      }
    }
  }

  /**
   * 2026.02.11 : 박은정 : 이벤트 전달
   */
  onEvent(eventType: string, eventData: unknown) {
    this.notify(eventType, eventData);
  }

  /**
   * 2026.02.11 : 박은정 : 리소스 정리
   */
  onDestroy() {
    this.outputs.collider = null;
    this.outputs.objectRoot = null;

    if (this.canvasTexture) {
      this.canvasTexture.dispose();
      this.canvasTexture = null;
    }
    if (this.mesh) {
      this.mesh.material.dispose();
      this.mesh.geometry.dispose();
      this.mesh = null;
    }
    this.pivotNode = null;
    this.canvas = null;
    this.image = null;
  }

  // ==========================================================================
  // 이미지 로드
  // ==========================================================================

  /**
   * 2026.02.11 : 박은정 : 이미지 로드
   */
  private loadImage() {
    if (!this.inputs.src || !this.canvas) return;
    // 2026.02.11 : 박은정 : 디버깅 로그 제거

    const that = this;
    this.image = new Image();
    this.image.crossOrigin = 'anonymous';

    this.image.onload = function () {
      // 2026.02.11 : 박은정 : 디버깅 로그 제거
      that.drawImageToCanvas();
    };

    this.image.onerror = function (e) {
      // 2026.02.11 : 박은정 : 디버깅 로그 제거
      that.drawPlaceholder('ERROR');
    };

    this.image.src = this.inputs.src;
  }

  // ==========================================================================
  // 캔버스 렌더링
  // ==========================================================================

  /**
   * 2026.02.11 : 박은정 : 로드된 이미지를 캔버스에 그리기
   */
  private drawImageToCanvas() {
    if (!this.canvas || !this.image || !this.canvasTexture) return;

    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;

    ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
    ctx.drawImage(this.image, 0, 0, this.canvas.width, this.canvas.height);

    this.canvasTexture.needsUpdate = true;
  }

  /**
   * 2026.02.11 : 박은정 : 플레이스홀더 그리기
   */
  private drawPlaceholder(text: string) {
    if (!this.canvas) return;

    const ctx = this.canvas.getContext('2d');
    if (!ctx) return;

    const w = this.canvas.width;
    const h = this.canvas.height;

    ctx.fillStyle = text === 'ERROR' ? '#cc3333' : '#336699';
    ctx.fillRect(0, 0, w, h);

    ctx.strokeStyle = '#ffffff';
    ctx.lineWidth = 4;
    ctx.strokeRect(4, 4, w - 8, h - 8);

    ctx.fillStyle = '#ffffff';
    ctx.font = 'bold ' + Math.floor(w / 8) + 'px Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(text, w / 2, h / 2);

    if (this.canvasTexture) {
      this.canvasTexture.needsUpdate = true;
    }
  }
}

// ============================================================================
// 컴포넌트 팩토리 등록
// ============================================================================

export const quadImagePlaneType = 'mp.quadImagePlane';
export function makeQuadImagePlane() {
  return new QuadImagePlane();
}
